// React
import React, { useState } from 'react';
import PropTypes from 'prop-types';

// Assets
import {
    ExclamationCircleOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined
} from '@ant-design/icons';

// Components
import Avatar from 'components/@extended/Avatar';
import { PopupTransition } from 'components/@extended/Transitions';

// Packages
import { Button, Dialog, DialogContent, Stack, Typography } from '@mui/material';

// Enum for modal types
export const ConfirmModalType = {
    SUCCESS: 'success',
    WARNING: 'warning',
    ERROR: 'error'
};

// Icon and color mapping based on modal type
const typeConfig = {
    [ConfirmModalType.SUCCESS]: {
        icon: <CheckCircleOutlined />,
        color: 'success'
    },
    [ConfirmModalType.WARNING]: {
        icon: <ExclamationCircleOutlined />,
        color: 'warning'
    },
    [ConfirmModalType.ERROR]: {
        icon: <CloseCircleOutlined />,
        color: 'error'
    }
};

const ConfirmModal = ({
    title,
    text,
    open,
    onClose,
    onConfirm,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    type = ConfirmModalType.WARNING,
    closeOnConfirm = true,
    disableConfirm = false,
    loadingText = 'Saving...'
}) => {
    const { icon, color } = typeConfig[type] || typeConfig[ConfirmModalType.WARNING];
    const [submitting, setSubmitting] = useState(false);

    const handleConfirm = async () => {
        try {
            setSubmitting(true);
            await onConfirm?.();

            if (closeOnConfirm) onClose?.();
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={submitting ? undefined : onClose}
            keepMounted
            TransitionComponent={PopupTransition}
            maxWidth="xs"
            aria-labelledby="item-confirm-title"
            aria-describedby="item-confirm-description"
            PaperProps={{
                sx: { borderRadius: '12px' }
            }}
        >
            <DialogContent sx={{ mt: 2, my: 1 }}>
                <Stack alignItems="center" spacing={3.5}>
                    {/*<Avatar
                        color={color}
                        sx={{ width: 72, height: 72, fontSize: '1.75rem' }}
                    >
                        {icon}
                    </Avatar>*/}
                    <Stack spacing={2}>
                        <Typography variant="h4" align="center">
                            {title}
                        </Typography>
                        <Typography align="center">{text}</Typography>
                    </Stack>

                    <Stack direction="row" spacing={2} sx={{ width: 1 }}>
                        <Button
                            fullWidth
                            color="secondary"
                            variant="outlined"
                            onClick={onClose}
                            disabled={submitting}
                            sx={{ borderRadius: '8px' }}
                        >
                            {cancelText}
                        </Button>
                        <Button
                            fullWidth
                            color={color}
                            variant="contained"
                            onClick={handleConfirm}
                            autoFocus
                            disabled={disableConfirm || submitting}
                            sx={{ borderRadius: '8px' }}
                        >
                            {submitting ? loadingText : confirmText}
                        </Button>
                    </Stack>
                </Stack>
            </DialogContent>
        </Dialog>
    );
};

ConfirmModal.propTypes = {
    title: PropTypes.string,
    text: PropTypes.node,
    open: PropTypes.bool,
    onClose: PropTypes.func,
    onConfirm: PropTypes.func,
    confirmText: PropTypes.string,
    cancelText: PropTypes.string,
    type: PropTypes.oneOf(Object.values(ConfirmModalType)),
    closeOnConfirm: PropTypes.bool,
    disableConfirm: PropTypes.bool,
    loadingText: PropTypes.string
};

export default ConfirmModal;
